import type { Role } from '@bia-auction/shared-schema';

/**
 * Role-based button/nav visibility, encoding the `docs/08-permissions-matrix.md` action→role
 * sets once (ADR 0019). Roles are additive tags with no hierarchy — a caller passes if it
 * holds ANY allowed role (mirrors the API's `requireRole`). The server re-checks every action;
 * this only hides controls the user can't use.
 */
export const PERMS = {
  salesCreate: ['ADMIN', 'DEVELOPER', 'EDITOR'],
  salesEdit: ['ADMIN', 'DEVELOPER', 'EDITOR'],
  salesDelete: ['ADMIN', 'DEVELOPER', 'EDITOR'],
  // SalesList — who may read the sale list and the overview counters. Mirrors the API's
  // `SALE_LIST_ROLES` (`routes/sales.ts`), which is **wider than `salesView`** by exactly
  // `FIRM_VENDOR`: a vendor sees the list but not a sale's detail, because the detail carries
  // bids. This is also the admin app's floor — a `BIDDER` or `USER` signing in on the admin
  // origin holds none of these roles and so reaches neither the overview nor the list.
  salesList: [
    'ADMIN',
    'DEVELOPER',
    'EDITOR',
    'FIRM',
    'FIRM_AUCTION',
    'FIRM_USER',
    'FIRM_VENDOR',
  ],
  // SalesView (docs/08) — who can open a sale's detail. FIRM_VENDOR may see the list but NOT
  // the detail, so the list must not link it to a guaranteed 403.
  salesView: ['ADMIN', 'DEVELOPER', 'EDITOR', 'FIRM', 'FIRM_AUCTION', 'FIRM_USER'],
  // pause / resume / set-reserve / take-control / manual close. Identical to salesCreate since
  // the M17 editor merge — the two differed only by STOCK_EDITOR — but kept as its own key
  // because they are separate rows in the matrix and may diverge again.
  salesLifecycle: ['ADMIN', 'DEVELOPER', 'EDITOR'],
  reAdd: ['ADMIN', 'DEVELOPER', 'EDITOR'],
  // Placing a console bid goes through `/bids` + `SaleDO.placeBid`, which only ADMIN/DEVELOPER
  // bypass the deposit/eligibility gate for — so, although docs/08 AdminBid also lists EDITOR,
  // granting it here would only surface controls that 403/reject. Keep it to the roles the
  // engine actually accepts.
  adminBid: ['ADMIN', 'DEVELOPER'],
  adminFreeBid: ['ADMIN', 'DEVELOPER'],
  // ExportSale (docs/08) — plus company-scoped FIRM_AUCTION.
  exportSale: ['ADMIN', 'DEVELOPER', 'EDITOR', 'FIRM_AUCTION'],
  depositView: ['ADMIN', 'DEVELOPER', 'EDITOR'],
  // DepositRefund (docs/08, M10) — narrower than DepositView: money movement is ADMIN/DEV only.
  depositRefund: ['ADMIN', 'DEVELOPER'],
  // AuditView (docs/08, M10) — the audit-log viewer page.
  auditView: ['ADMIN', 'DEVELOPER'],
  conferenceView: ['ADMIN', 'DEVELOPER', 'EDITOR'],
  userAdmin: ['ADMIN', 'DEVELOPER'],
  // CompanyAdmin (docs/08, M13) — firm records AND the ingestion API keys that hang off them.
  // Same set that already read the company directory for the sale/user pickers: issuing a
  // partner's credential is a sale-operations job, not user administration.
  companyAdmin: ['ADMIN', 'DEVELOPER', 'EDITOR'],
  configAdmin: ['ADMIN', 'DEVELOPER'],
  submissions: ['ADMIN', 'DEVELOPER', 'EDITOR'],
  // VehicleView / VehicleEdit (docs/08, M11). View is deliberately WIDER than SalesView:
  // SalesView stops at FIRM_AUCTION/FIRM_USER because a sale's detail carries bids, which are
  // competitively sensitive between firms — a vehicle record is stock data, and FIRM_VENDOR is
  // the firm-side role that owns it (having absorbed FIRM_STOCK in M17), so every firm
  // sub-role reads the pool. Mirrors `VEHICLE_VIEW_ROLES` in the API's `lib/sale-visibility.ts`;
  // the hidden reserve is stripped for every firm role independently (ADR 0013). Edit stays
  // narrow — though "narrow" now means the one editor role, so a former SALE_EDITOR can edit
  // vehicles where they previously could not.
  vehicleView: [
    'ADMIN',
    'DEVELOPER',
    'EDITOR',
    'FIRM',
    'FIRM_AUCTION',
    'FIRM_USER',
    'FIRM_VENDOR',
  ],
  vehicleEdit: ['ADMIN', 'DEVELOPER', 'EDITOR'],
} as const satisfies Record<string, readonly Role[]>;

export type Permission = keyof typeof PERMS;

/** True if `roles` includes any of `allowed`. */
export function hasAnyRole(roles: Role[], allowed: readonly Role[]): boolean {
  return roles.some((r) => allowed.includes(r));
}

/** True if `roles` may perform `perm`. */
export function can(roles: Role[], perm: Permission): boolean {
  return hasAnyRole(roles, PERMS[perm]);
}

/**
 * The permission each admin route requires, or `null` for one open to every signed-in operator
 * (M19, audit finding O-06).
 *
 * **One map, two consumers**: `router.tsx` turns each entry into a `beforeLoad` guard, and
 * `AppShell`'s nav turns it into `show`. Before this they were separate expressions, and the
 * routes drifted from the nav — `/users`, `/audit` and `/config` opened by direct URL for an
 * operator whose sidebar never offered them. Deriving both from here means a route can no
 * longer be reachable by URL but hidden in the nav, or the reverse, by accident.
 *
 * The open entries are deliberate, not omissions:
 * - `/` and `/sales` require `salesList`, the admin app's floor: the API gates both to
 *   `SALE_LIST_ROLES`. Which *rows* an operator sees inside them stays firm-scoped
 *   server-side; the permission only decides who may ask at all.
 * - `/help` is open because an operator needs to know an action exists even where they cannot
 *   perform it; each handbook topic names the roles that can.
 * - `/forbidden` is where this map sends people, so gating it would be a loop.
 */
export const ROUTE_PERMISSIONS = {
  // Not `null`: the API gates both the overview counters and the sale list to
  // `SALE_LIST_ROLES`, so an account with none of them — a bidder who signs in on the admin
  // origin — would otherwise pass the guard, see the entries in the sidebar, and watch the
  // page render against 403ing queries. That is the same nav-vs-reachability drift M19 exists
  // to close, reached through the "open route" path. Firm *scoping* of the rows stays
  // server-side; this only decides who may ask.
  '/': 'salesList',
  '/sales': 'salesList',
  '/sales/new': 'salesCreate',
  '/sales/$saleId': 'salesView',
  '/vehicles': 'vehicleView',
  '/vehicles/new': 'vehicleEdit',
  // `vehicleView`, not `vehicleEdit`: `docs/08-permissions-matrix.md` scopes VehicleView to the
  // list **and detail**, the API's `GET /admin/vehicles/:id` uses `VEHICLE_VIEW_ROLES`, and the
  // page already renders read-only for anyone without `vehicleEdit`. Guarding it on edit would
  // send every firm role to /forbidden from a row their own vehicle list linked them to.
  '/vehicles/$vehicleId': 'vehicleView',
  '/submissions': 'submissions',
  '/firms': 'companyAdmin',
  '/firms/$companyId': 'companyAdmin',
  '/users': 'userAdmin',
  '/users/$userId': 'userAdmin',
  '/audit': 'auditView',
  '/config': 'configAdmin',
  '/help': null,
  '/forbidden': null,
} as const satisfies Record<string, Permission | null>;

export type AdminRoute = keyof typeof ROUTE_PERMISSIONS;

/** True if `roles` may open `route`. Routes with no permission are open to any session. */
export function canOpen(roles: Role[], route: AdminRoute): boolean {
  const perm = ROUTE_PERMISSIONS[route];
  return perm === null || can(roles, perm);
}
